import { useEffect, useState } from 'react';

export interface ApproveCategoryItem {
  name: string;
  planQuantity: number;
  planAmount: number;
}

interface ApproveModalProps {
  isOpen: boolean;
  monthLabel?: string;
  department?: string;
  submitter?: string;
  createdAt?: string;
  categories?: ApproveCategoryItem[];
  submitting?: boolean;
  onCancel: () => void;
  onApprove: (remark: string) => void;
  onReject: (remark: string) => void;
}

export default function ApproveModal({
  isOpen,
  monthLabel = '',
  department = '',
  submitter = '',
  createdAt = '',
  categories = [],
  submitting = false,
  onCancel,
  onApprove,
  onReject,
}: ApproveModalProps) {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setRemark('');
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleReject = () => {
    const text = remark.trim();
    if (!text) {
      setError('需要填写审批意见');
      return;
    }
    setError('');
    onReject(text);
  };

  const handleApprove = () => {
    setError('');
    onApprove(remark.trim());
  };

  const formatSubmitTime = (time: string) => {
    if (!time) return '-';
    const d = new Date(time);
    if (Number.isNaN(d.getTime())) return time;
    return d.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div
        className="bg-background-50 rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-2 shrink-0">
          <i className="ri-shield-check-line text-primary-500 text-lg" />
          <h3 className="text-sm font-semibold text-foreground-900">审批操作</h3>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-foreground-500 mb-0.5">计划月份</p>
              <p className="font-medium text-foreground-900">{monthLabel || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500 mb-0.5">提交时间</p>
              <p className="font-medium text-foreground-900">{formatSubmitTime(createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500 mb-0.5">科室名称</p>
              <p className="font-medium text-foreground-900">{department || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-foreground-500 mb-0.5">提交人</p>
              <p className="font-medium text-foreground-900">{submitter || '-'}</p>
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-700 mb-2">分类汇总</p>
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-background-100 text-foreground-600">
                    <th className="py-2 px-3 text-left font-medium">分类</th>
                    <th className="py-2 px-3 text-center font-medium whitespace-nowrap">计划数量</th>
                    <th className="py-2 px-3 text-right font-medium whitespace-nowrap">计划金额</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-6 px-3 text-center text-foreground-400">
                        暂无分类汇总
                      </td>
                    </tr>
                  ) : (
                    categories.map((item, idx) => (
                      <tr
                        key={`${item.name}-${idx}`}
                        className={`border-t border-background-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-background-50/40'}`}
                      >
                        <td className="py-2 px-3 text-foreground-800">{item.name || '未分类'}</td>
                        <td className="py-2 px-3 text-center text-foreground-800 whitespace-nowrap">
                          {item.planQuantity}
                        </td>
                        <td className="py-2 px-3 text-right text-foreground-800 whitespace-nowrap">
                          ¥{item.planAmount.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1.5 ${error ? 'text-red-600' : 'text-foreground-700'}`}>
              审批批注
            </label>
            <textarea
              value={remark}
              onChange={(e) => {
                const next = e.target.value;
                setRemark(next);
                if (error && next.trim()) setError('');
              }}
              placeholder="填写批注或驳回原因..."
              maxLength={1000}
              rows={3}
              disabled={submitting}
              aria-invalid={!!error}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg outline-none resize-none disabled:opacity-60 transition-colors ${
                error
                  ? 'border-red-400 bg-red-50/30 focus:border-red-500 focus:ring-1 focus:ring-red-100'
                  : 'border-background-300 bg-transparent focus:border-primary-400 focus:ring-1 focus:ring-primary-200'
              }`}
            />
            <div className="mt-1.5 flex items-start justify-between gap-2">
              {error ? (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {error}
                </p>
              ) : (
                <p className="text-xs text-foreground-500">同意时批注为选填；拒绝时批注为驳回原因（必填）。</p>
              )}
              <p className="text-xs text-foreground-400 shrink-0">{remark.length}/1000</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            关闭
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
            style={{ backgroundColor: '#ed4014' }}
          >
            <i className="ri-close-line mr-1" />
            拒绝
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? (
              <i className="ri-loader-4-line animate-spin mr-1" />
            ) : (
              <i className="ri-check-line mr-1" />
            )}
            同意
          </button>
        </div>
      </div>
    </div>
  );
}
